import { useEffect, useRef, useState } from "react";
import type {
  CommentStatus,
  CommentType,
  CommentVisibility,
  MentionRef,
  VisibilityChange,
  WorkspaceMember,
} from "../../api/types";
import {
  Check,
  CheckCircle2,
  MoreHorizontal,
  PenLine,
  Pin,
  Repeat2,
  RotateCcw,
  Trash2,
} from "../../compose/Icon";
import { CopyLinkButton } from "../CopyLinkButton";
import { CommentVisibilityPill } from "./CommentVisibilityPill";
import { TYPE_GLYPH, TYPE_LABEL } from "./TypeBadge";

/**
 * Human label per review status. Re-homed here from the retired
 * `CommentStatusControls` — this bar is the ONLY surface that renders it, and
 * the old module's badge/button-row components had no importers left after the
 * rich-comments §11 redesign folded them into this row.
 */
export const STATUS_LABEL: Record<CommentStatus, string> = {
  open: "Open",
  resolved: "Resolved",
  acted_on: "Acted-on",
  awaiting_re_review: "Re-review",
  pinned: "Pinned",
};

/**
 * Row-1 of the redesigned comment card (rich-comments §11): a type-COLORED
 * status+type pill ("Open Comment" / "Resolved Suggestion"), a primary Resolve /
 * Reopen button, a ⋯ overflow menu of the remaining status transitions (incl.
 * "Acted-on"), and an icon-only Copy-link — plus a leftmost Pin toggle. Same
 * `onStatus` wiring as the old `<CommentStatusControls>` (a layout move, not a
 * behaviour change); the pill's `data-type`/`data-status` drive the §12 colors.
 */

/** A small affix glyph per status so color is never the only signal (a11y). */
function StatusAffix({ status }: { status: CommentStatus }) {
  if (status === "resolved") return <Check size={11} strokeWidth={2} />;
  if (status === "acted_on") return <CheckCircle2 size={11} strokeWidth={2} />;
  if (status === "awaiting_re_review") return <Repeat2 size={11} strokeWidth={2} />;
  if (status === "pinned") return <Pin size={11} strokeWidth={2} />;
  return null;
}

export function CommentCardBar({
  commentId,
  type,
  status,
  busy,
  onStatus,
  slug,
  docId,
  onCopyLink,
  visibility,
  sharedWith,
  members,
  canEditVisibility,
  visibilityBusy,
  onVisibility,
  canEdit,
  onEdit,
  canDelete,
  onDelete,
  deleted,
}: {
  commentId: number;
  type: CommentType;
  status: CommentStatus;
  busy?: boolean;
  onStatus: (next: CommentStatus) => void;
  slug: string;
  docId: string;
  onCopyLink?: (url: string) => void;
  // ── review-notes §3 author edit: the "Edit" overflow-menu item, shown only
  //    when the viewer authored this note (`canEdit`) + an `onEdit` is wired. ──
  canEdit?: boolean;
  onEdit?: () => void;
  // ── comment-delete: the "Delete" overflow-menu item, shown only when the
  //    viewer AUTHORED this note (`canDelete`) and an `onDelete` is wired. It
  //    lives at the BOTTOM of the menu, separated, because it is the only
  //    irreversible item in a list of reversible status changes — a destructive
  //    action adjacent to "Pin" is a mis-click waiting to happen. ─────────────
  canDelete?: boolean;
  onDelete?: () => void;
  /** True when this card is a TOMBSTONE (a deleted note kept only to hold its
   *  replies together). Every ACTION on the bar is suppressed: there is nothing
   *  left to resolve, pin, re-scope, copy a link to, or delete again. */
  deleted?: boolean;
  // ── comment-visibility (§6A): the interactive scope pill. Rendered only when
  //    `onVisibility` is wired (root cards); replies inherit + show no pill. ───
  visibility?: CommentVisibility;
  sharedWith?: MentionRef[];
  members?: WorkspaceMember[];
  /** author-or-staff — false ⇒ the popup opens read-only (§6A.7). */
  canEditVisibility?: boolean;
  visibilityBusy?: boolean;
  onVisibility?: (body: VisibilityChange) => Promise<void> | void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const TypeGlyph = TYPE_GLYPH[type];
  const resolved = status === "resolved";
  const pinned = status === "pinned";

  // Dismiss the overflow menu on outside-click / Esc.
  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const pick = (next: CommentStatus) => {
    setMenuOpen(false);
    onStatus(next);
  };

  const pickEdit = () => {
    setMenuOpen(false);
    onEdit?.();
  };
  const showEdit = canEdit === true && onEdit != null;
  const pickDelete = () => {
    setMenuOpen(false);
    onDelete?.();
  };
  const showDelete = canDelete === true && onDelete != null;

  // The remaining transitions live in the ⋯ menu (Resolve is the primary button).
  const menuItems: { status: CommentStatus; label: string; icon: React.ReactNode }[] = [
    { status: "acted_on", label: "Acted-on", icon: <CheckCircle2 size={13} strokeWidth={1.75} /> },
    { status: "awaiting_re_review", label: "Re-review", icon: <Repeat2 size={13} strokeWidth={1.75} /> },
    pinned
      ? { status: "open", label: "Unpin", icon: <Pin size={13} strokeWidth={1.75} /> }
      : { status: "pinned", label: "Pin", icon: <Pin size={13} strokeWidth={1.75} /> },
  ];
  if (status !== "open" && !pinned) {
    menuItems.push({ status: "open", label: "Reopen", icon: <RotateCcw size={13} strokeWidth={1.75} /> });
  }

  // comment-delete: a TOMBSTONE gets a bar with no actions at all. It still shows
  // WHAT it was (the type/status pill) so the thread reads coherently, but there
  // is nothing left to resolve, pin, re-scope, react to, link to, or delete —
  // and offering any of those on a note with no content is an invitation to a
  // write that means nothing.
  if (deleted === true) {
    return (
      <div
        className="comment-card__bar comment-card__bar--deleted"
        data-testid={`card-bar-${commentId}`}
        data-deleted=""
      >
        <span
          className="comment-card__statustype"
          data-testid={`card-statustype-${commentId}`}
          data-type={type}
          data-status={status}
        >
          <Trash2 size={12} strokeWidth={1.75} />
          Deleted {TYPE_LABEL[type]}
        </span>
        <span className="comment-card__bar-spacer" />
      </div>
    );
  }

  return (
    <div className="comment-card__bar" data-testid={`card-bar-${commentId}`}>
      <button
        type="button"
        className={`comment-card__pin${pinned ? " comment-card__pin--on" : ""}`}
        data-testid={`card-pin-${commentId}`}
        aria-pressed={pinned}
        aria-label={pinned ? "Unpin comment" : "Pin comment"}
        title={pinned ? "Unpin" : "Pin"}
        disabled={busy}
        onClick={() => onStatus(pinned ? "open" : "pinned")}
      >
        <Pin size={13} strokeWidth={1.75} />
      </button>

      {onVisibility ? (
        <CommentVisibilityPill
          commentId={commentId}
          visibility={visibility ?? "public"}
          sharedWith={sharedWith ?? []}
          members={members ?? []}
          canEdit={canEditVisibility ?? false}
          busy={visibilityBusy}
          onChange={onVisibility}
        />
      ) : null}

      <span
        className="comment-card__statustype"
        data-testid={`card-statustype-${commentId}`}
        data-type={type}
        data-status={status}
      >
        <TypeGlyph size={12} strokeWidth={1.75} />
        <StatusAffix status={status} />
        {STATUS_LABEL[status]} {TYPE_LABEL[type]}
      </span>

      <span className="comment-card__bar-spacer" />

      <button
        type="button"
        className="comment-card__resolve"
        data-testid={`card-resolve-${commentId}`}
        disabled={busy}
        onClick={() => onStatus(resolved ? "open" : "resolved")}
      >
        {resolved ? (
          <>
            <RotateCcw size={12} strokeWidth={1.75} /> Reopen
          </>
        ) : (
          <>
            <CheckCircle2 size={12} strokeWidth={1.75} /> Resolve
          </>
        )}
      </button>

      <div className="comment-card__menu-wrap" ref={menuRef}>
        <button
          type="button"
          className="comment-card__more"
          data-testid={`card-menu-${commentId}`}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label="More status actions"
          title="More"
          disabled={busy}
          onClick={() => setMenuOpen((o) => !o)}
        >
          <MoreHorizontal size={14} strokeWidth={1.75} />
        </button>
        {menuOpen ? (
          <div className="comment-card__menu" role="menu" data-testid={`card-menu-open-${commentId}`}>
            {showEdit ? (
              <button
                type="button"
                role="menuitem"
                className="comment-card__menu-item"
                data-testid={`card-menu-item-edit-${commentId}`}
                onClick={pickEdit}
              >
                <PenLine size={13} strokeWidth={1.75} />
                Edit
              </button>
            ) : null}
            {menuItems.map((m) => (
              <button
                key={`${m.status}-${m.label}`}
                type="button"
                role="menuitem"
                className={`comment-card__menu-item${status === m.status ? " comment-card__menu-item--active" : ""}`}
                data-testid={`card-menu-item-${m.status}-${commentId}`}
                disabled={status === m.status}
                onClick={() => pick(m.status)}
              >
                {m.icon}
                {m.label}
              </button>
            ))}
            {showDelete ? (
              <>
                {/* A rule, and last position: Delete is the only IRREVERSIBLE
                    item in a menu of reversible status changes. Putting it
                    adjacent to "Pin" is how a mis-click destroys a note. */}
                <span className="comment-card__menu-sep" role="separator" />
                <button
                  type="button"
                  role="menuitem"
                  className="comment-card__menu-item comment-card__menu-item--danger"
                  data-testid={`card-menu-item-delete-${commentId}`}
                  onClick={pickDelete}
                >
                  <Trash2 size={13} strokeWidth={1.75} />
                  Delete
                </button>
              </>
            ) : null}
          </div>
        ) : null}
      </div>

      <CopyLinkButton
        slug={slug}
        docId={docId}
        commentId={commentId}
        compact
        className="comment-card__copylink"
        onCopied={onCopyLink}
      />
    </div>
  );
}
